import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Sidebar from '../Sidebar';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import { message, Spin } from 'antd';
import doctorApi from '../../api/doctor.api';
import { visitApi } from '../../api/visit.api';

const DoctorProfileMedical = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [doctorData, setDoctorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [doctorStats, setDoctorStats] = useState({
    totalPatients: 0,
    totalVisitsThisMonth: 0,
    activeSickLeaves: 0,
    prescriptionsIssued: 0,
    mostCommonDiagnoses: []
  });
  const [recentVisits, setRecentVisits] = useState([]);
  const [recentPrescriptions, setRecentPrescriptions] = useState([]);
  const [recentPatients, setRecentPatients] = useState([]);

  // Load doctor data and statistics
  useEffect(() => {
    const loadDoctorData = async () => {
      try {
        setLoading(true);
        const doctorId = parseInt(id);
        
        // Load basic doctor information
        const doctor = await doctorApi.getDoctorById(doctorId);
        setDoctorData({
          id: doctor.id,
          uinNumber: doctor.uinNumber,
          fullName: doctor.fullName,
          firstName: doctor.firstName,
          lastName: doctor.lastName,
          specialties: doctor.speciality ? [doctor.speciality] : [],
          phone: doctor.phone,
          email: doctor.email,
          degree: doctor.degree,
          joiningDate: doctor.joiningDate,
          notes: doctor.notes,
          department: doctor.department,
          active: doctor.active
        });
        
        // Load doctor's visits and statistics
        await loadDoctorStatistics(doctorId);
        
      } catch (error) {
        console.error('Error loading doctor:', error);
        message.error('Грешка при зареждане на данните за лекаря');
        navigate('/doctors');
      } finally {
        setLoading(false);
      }
    };

    const loadDoctorStatistics = async (doctorId) => {
      try {
        // Get all visits for this doctor
        // Use statistics API instead of loading all visits
        const [statistics, recentVisits] = await Promise.all([
          visitApi.getDoctorStatistics(doctorId),
          visitApi.getRecentVisitsByDoctorId(doctorId, 10)
        ]);
        
        // Statistics are handled by setDoctorStats below

        // Update doctor stats with API data
        setDoctorStats({
          totalPatients: statistics.totalPatients,
          totalVisitsThisMonth: statistics.visitsThisMonth,
          activeSickLeaves: 0, // TODO: Implement when sick leave data is available
          prescriptionsIssued: statistics.totalPrescriptions || 0,
          mostCommonDiagnoses: Object.entries(statistics.topDiagnoses || {})
            .map(([name, count]) => ({ name, count, mkbCode: '' }))
            .slice(0, 5)
        });
        
        // Set recent visits from API
        setRecentVisits(recentVisits);
        
        // Extract recent patients from recent visits
        const patientMap = new Map();
        recentVisits.forEach(visit => {
          const patientKey = visit.patientId;
          if (!patientMap.has(patientKey)) {
            patientMap.set(patientKey, {
              patientId: visit.patientId,
              name: visit.patientName,
              egn: visit.patientEgn,
              lastVisitDate: visit.visitDate,
              totalVisits: 1, // We only have recent visits, so approximate
              lastDiagnosis: {
                mkbCode: visit.mainDiagnosisMkbCode || 'N/A',
                name: visit.mainDiagnosisName || 'Няма диагноза'
              }
            });
          }
        });
        
        // Convert to array and sort by last visit date
        const recentPatientsData = Array.from(patientMap.values())
          .sort((a, b) => new Date(b.lastVisitDate) - new Date(a.lastVisitDate));
        
        setRecentPatients(recentPatientsData);
        
        // Extract prescriptions from recent visits
        const prescriptions = [];
        recentVisits.forEach(visit => {
          try {
            const reimbursableDrugs = visit.reimbursableDrugs ? JSON.parse(visit.reimbursableDrugs) : [];
            reimbursableDrugs.forEach(drug => {
              prescriptions.push({
                visitId: visit.id,
                prescriptionNumber: `${visit.noAl}-${drug.drugCode}`,
                drugCode: drug.drugCode,
                mkbCode: drug.mkbCode,
                quantity: drug.quantity,
                days: drug.days,
                patientName: visit.patientName,
                visitDate: visit.visitDate
              });
            });
          } catch (e) {
            console.error('Error parsing prescription data:', e);
          }
        });
        
        // Sort prescriptions by visit date and take recent ones
        const sortedPrescriptions = prescriptions
          .sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate))
          .slice(0, 10);
        setRecentPrescriptions(sortedPrescriptions);
        
      } catch (error) {
        console.error('Error loading doctor statistics:', error);
        message.warning('Някои статистически данни не могат да бъдат заредени');
      }
    };

    if (id) {
      loadDoctorData();
    }
  }, [id, navigate]);


  if (loading) {
    return (
      <div>
        <Header />
        <Sidebar id="menu-item1" id1="menu-items1" activeClassName="doctor-medical-profile" />
        <div className="page-wrapper">
          <div className="content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  if (!doctorData) {
    return (
      <div>
        <Header />
        <Sidebar id="menu-item1" id1="menu-items1" activeClassName="doctor-medical-profile" />
        <div className="page-wrapper">
          <div className="content">
            <div className="alert alert-danger">Лекарят не е намерен</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <Sidebar
        id="menu-item1"
        id1="menu-items1"
        activeClassName="doctor-medical-profile"
      />
      
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/doctors">Лекари</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FeatherIcon icon="chevron-right" />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Медицински профил</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Doctor Header Card */}
          <div className="row">
            <div className="col-md-12">
              <div className="card doctor-header-card">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="doctor-info">
                        <h3 className="mb-1">{doctorData.fullName}</h3>
                        <p className="text-muted mb-1">УИН: {doctorData.uinNumber}</p>
                        <p className="text-muted mb-1">
                          Специалности: {doctorData.specialties.join(', ')}
                        </p>
                        {doctorData.department && (
                          <p className="text-muted mb-0">
                            <FeatherIcon icon="building" size={14} className="me-1" />
                            Отделение: {doctorData.department.name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="doctor-actions">
                        <Link to="/visits/new" className="btn btn-light me-2">
                          <FeatherIcon icon="plus" size={14} className="me-1" />
                          Нов преглед
                        </Link>
                        <Link to={`/doctors/edit/${doctorData.id}`} className="btn btn-outline-light">
                          <FeatherIcon icon="edit" size={14} className="me-1" />
                          Редактирай
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Doctor Details */}
          <div className="row">
            {/* Basic Information */}
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">
                    <FeatherIcon icon="user" size={18} className="me-2" />
                    Основна информация
                  </h5>
                </div>
                <div className="card-body">
                  <div className="info-list">
                    <div className="info-item">
                      <label>УИН номер:</label>
                      <span className="badge bg-primary">{doctorData.uinNumber}</span>
                    </div>
                    {doctorData.degree && (
                      <div className="info-item">
                        <label>Образование:</label>
                        <span>{doctorData.degree}</span>
                      </div>
                    )}
                    {doctorData.joiningDate && (
                      <div className="info-item">
                        <label>Дата на назначаване:</label>
                        <span>{new Date(doctorData.joiningDate).toLocaleDateString('bg-BG')}</span>
                      </div>
                    )}
                    <div className="info-item">
                      <label>Статус:</label>
                      <span className={`badge ${doctorData.active ? 'bg-success' : 'bg-danger'}`}>
                        {doctorData.active ? 'Активен' : 'Неактивен'}
                      </span>
                    </div>
                    <div className="info-item">
                      <label>Телефон:</label>
                      <span>{doctorData.phone}</span>
                    </div>
                    <div className="info-item">
                      <label>Имейл:</label>
                      <span>{doctorData.email}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {doctorData.notes && (
                <div className="card">
                  <div className="card-header">
                    <h5 className="card-title">
                      <FeatherIcon icon="file-text" size={18} className="me-2" />
                      Бележки
                    </h5>
                  </div>
                  <div className="card-body">
                    <p className="mb-0">{doctorData.notes}</p>
                  </div>
                </div>
              )}

              {/* Statistics */}
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">
                    <FeatherIcon icon="bar-chart-2" size={18} className="me-2" />
                    Статистика
                  </h5>
                </div>
                <div className="card-body">
                  <div className="info-list">
                    <div className="info-item">
                      <label>Общо пациенти:</label>
                      <span className="badge bg-secondary">{doctorStats.totalPatients}</span>
                    </div>
                    <div className="info-item">
                      <label>Посещения този месец:</label>
                      <span className="badge bg-secondary">{doctorStats.totalVisitsThisMonth}</span>
                    </div>
                    <div className="info-item">
                      <label>Активни болнични:</label>
                      <span className="badge bg-secondary">{doctorStats.activeSickLeaves}</span>
                    </div>
                    <div className="info-item">
                      <label>Изписани рецепти:</label>
                      <span className="badge bg-secondary">{doctorStats.prescriptionsIssued}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Patients */}
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">
                    <FeatherIcon icon="users" size={18} className="me-2" />
                    Последни пациенти
                  </h5>
                </div>
                <div className="card-body">
                  <div className="patient-list">
                    {recentPatients.length === 0 ? (
                      <div className="text-center text-muted py-3">
                        <FeatherIcon icon="users" size={32} className="mb-2" />
                        <p className="mb-0">Няма данни за пациенти</p>
                      </div>
                    ) : (
                      recentPatients.slice(0, 5).map((patient, index) => (
                        <div key={index} className="patient-item">
                          <div className="patient-info">
                            <Link 
                              to={`/patients/medical-profile/${patient.patientId}`} 
                              className="text-decoration-none"
                            >
                              <h6 className="mb-1 text-primary">{patient.name}</h6>
                            </Link>
                            <small className="text-muted">ЕГН: {patient.egn}</small>
                            <div className="patient-meta">
                              <small className="text-muted">
                                Последно: {new Date(patient.lastVisitDate).toLocaleDateString('bg-BG')} | Посещения: {patient.totalVisits}
                              </small>
                            </div>
                            <div className="diagnosis-info">
                              <small className="text-primary">
                                {patient.lastDiagnosis.mkbCode}: {patient.lastDiagnosis.name}
                              </small>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {recentPatients.length > 0 && (
                    <Link to="/patients" className="btn btn-sm btn-outline-primary mt-2">
                      Виж всички пациенти
                    </Link>
                  )}
                </div>
              </div>

              {/* Common Diagnoses */}
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">
                    <FeatherIcon icon="trending-up" size={18} className="me-2" />
                    Най-чести диагнози
                  </h5>
                </div>
                <div className="card-body">
                  <div className="diagnosis-stats">
                    {doctorStats.mostCommonDiagnoses.length === 0 ? (
                      <div className="text-center text-muted py-3">
                        <FeatherIcon icon="trending-up" size={32} className="mb-2" />
                        <p className="mb-0">Няма данни за диагнози</p>
                      </div>
                    ) : (
                      doctorStats.mostCommonDiagnoses.map((diagnosis, index) => (
                        <div key={index} className="diagnosis-stat-item">
                          <div className="diagnosis-info">
                            <h6 className="mb-1">{diagnosis.name}</h6>
                            <small className="text-muted">{diagnosis.mkbCode}</small>
                          </div>
                          <span className="badge bg-info">{diagnosis.count}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">
                    <FeatherIcon icon="activity" size={18} className="me-2" />
                    Последни прегледи
                  </h5>
                </div>
                <div className="card-body">
                  <div className="visit-list">
                    {recentVisits.length === 0 ? (
                      <div className="text-center text-muted py-3">
                        <FeatherIcon icon="activity" size={32} className="mb-2" />
                        <p className="mb-0">Няма данни за прегледи</p>
                      </div>
                    ) : (
                      recentVisits.slice(0, 4).map((visit, index) => {
                        const getVisitTypeLabel = (visit) => {
                          if (visit.consultationVisit) return "Консултация";
                          if (visit.dispensaryVisit) return "Диспансер";
                          if (visit.babyCareVisit) return "Грижи за бебе";
                          if (visit.hospitalReferral) return "Болнично насочване";
                          if (visit.unfavorableVisit) return "Неблагоприятен случай";
                          if (visit.lkkVisit) return "ЛКК посещение";
                          if (visit.telkVisit) return "ТЕЛК посещение";
                          if (visit.prophylacticOver18) return "Профилактика 18+";
                          if (visit.prophylacticChild) return "Детска профилактика";
                          if (visit.prophylacticMother) return "Майчина профилактика";
                          if (visit.prophylacticRisk) return "Рискова профилактика";
                          return "Стандартен";
                        };

                        return (
                          <div key={index} className="visit-item">
                            <div className="visit-header">
                              <div className="visit-meta">
                                <h6 className="mb-1">{getVisitTypeLabel(visit)}</h6>
                                <small className="text-muted">
                                  {new Date(visit.visitDate).toLocaleDateString('bg-BG')} в {visit.visitTime}
                                </small>
                              </div>
                              <span className="badge bg-success">{visit.signed ? 'Подписан' : 'В процес'}</span>
                            </div>
                            <div className="visit-details">
                              <p className="mb-1 small text-primary">№ {visit.noAl}</p>
                              <p className="mb-1 small font-weight-bold">{visit.patientName}</p>
                              {visit.mainDiagnosisName && visit.mainDiagnosisMkbCode && (
                                <p className="mb-0 small">
                                  <strong>{visit.mainDiagnosisMkbCode}:</strong> {visit.mainDiagnosisName}
                                </p>
                              )}
                              <div className="visit-badges mt-1">
                                <span className="badge bg-secondary">
                                  {visit.examType === 11 ? 'Първичен' : 
                                   visit.examType === 12 ? 'Контролен' : 
                                   visit.examType === 13 ? 'Профилактичен' : 'Неизвестен'}
                                </span>
                                {/* TODO: Add sick leave badge when data is available */}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {recentVisits.length > 0 && (
                    <Link to="/visits" className="btn btn-sm btn-outline-primary mt-2">
                      Виж всички прегледи
                    </Link>
                  )}
                </div>
              </div>

              {/* Recent Prescriptions */}
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">
                    <FeatherIcon icon="file-text" size={18} className="me-2" />
                    Последни рецепти
                  </h5>
                </div>
                <div className="card-body">
                  <div className="prescription-list">
                    {recentPrescriptions.length === 0 ? (
                      <div className="text-center text-muted py-3">
                        <FeatherIcon icon="file-text" size={32} className="mb-2" />
                        <p className="mb-0">Няма данни за рецепти</p>
                      </div>
                    ) : (
                      recentPrescriptions.slice(0, 5).map((prescription, index) => (
                        <div key={index} className="prescription-item">
                          <div className="prescription-header">
                            <h6 className="mb-1">Рецепта № {prescription.prescriptionNumber}</h6>
                            <span className="badge bg-info">{prescription.mkbCode}</span>
                          </div>
                          <div className="prescription-details">
                            <p className="mb-1 small font-weight-bold">{prescription.patientName}</p>
                            <p className="mb-1 small">
                              <strong>Код:</strong> {prescription.drugCode} | 
                              <strong> Кол:</strong> {prescription.quantity} | 
                              <strong> Дни:</strong> {prescription.days}
                            </p>
                            <small className="text-muted">
                              {new Date(prescription.visitDate).toLocaleDateString('bg-BG')}
                            </small>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .doctor-header-card {
          background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
          color: white;
          margin-bottom: 20px;
        }
        
        .doctor-header-card .text-muted {
          color: rgba(255, 255, 255, 0.8) !important;
        }
        
        .info-list {
          padding: 0;
        }
        
        .info-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .info-item:last-child {
          border-bottom: none;
        }
        
        .info-item label {
          font-weight: 500;
          margin-bottom: 0;
          color: #666;
        }
        
        .patient-item {
          padding: 12px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .patient-item:last-child {
          border-bottom: none;
        }
        
        .patient-meta {
          margin: 4px 0;
        }
        
        .diagnosis-info {
          margin-top: 4px;
        }
        
        .visit-item {
          padding: 12px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .visit-item:last-child {
          border-bottom: none;
        }
        
        .visit-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }
        
        .visit-details {
          margin-left: 0;
        }
        
        .visit-badges {
          margin-top: 6px;
        }
        
        .prescription-item {
          padding: 12px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .prescription-item:last-child {
          border-bottom: none;
        }
        
        .prescription-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }
        
        .diagnosis-stat-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .diagnosis-stat-item:last-child {
          border-bottom: none;
        }
        
        .card {
          margin-bottom: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #dee2e6;
        }
      `}</style>
    </div>
  );
};

export default DoctorProfileMedical;